import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Download, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReadingPage = () => {
  const { readingId } = useParams();
  const navigate = useNavigate();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReading();
  }, [readingId]);

  const fetchReading = async () => {
    if (!readingId || readingId.length < 10) {
      toast.error('Invalid reading ID');
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${API}/reading/${readingId}`, {
        timeout: 10000
      });
      
      if (!response.data) {
        throw new Error('No reading data received');
      }
      
      setReading(response.data);
    } catch (error) {
      console.error('Fetch reading error:', error);
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || error.message;
      
      if (status === 404) {
        toast.error('Reading not found. It may have been deleted.');
      } else {
        toast.error(`Failed to load reading: ${errorMsg}`);
      }
      
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!reading) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;

    doc.setFontSize(24);
    doc.text('Your Numerology Reading', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    doc.setFontSize(12);
    doc.text(`Name: ${reading.name}`, margin, yPosition);
    yPosition += 7;
    doc.text(`Birthday: ${reading.birthday}`, margin, yPosition);
    yPosition += 15;

    const numerologyPdfHeader = `Life Path: ${reading.life_path_number || 'N/A'}, Chinese: ${reading.chinese_zodiac || 'N/A'}, Western: ${reading.western_zodiac || 'N/A'}, Qua: ${reading.qua_number} (${reading.feng_shui_element})`;
    
    const chineseCompatPdf = reading.chinese_compatibility ? 
      `Best: ${reading.chinese_compatibility.best.join(', ')} | Challenging: ${reading.chinese_compatibility.challenging.join(', ')}` : '';
    
    const westernCompatPdf = reading.western_compatibility ? 
      `Best: ${reading.western_compatibility.best.join(', ')} | Challenging: ${reading.western_compatibility.challenging.join(', ')}` : '';
    
    const fengShuiPdfContent = reading.feng_shui_directions ? 
      `Success: ${reading.feng_shui_directions.success}, ` +
      `Health: ${reading.feng_shui_directions.health}, ` +
      `Romance: ${reading.feng_shui_directions.romance}, ` +
      `Growth: ${reading.feng_shui_directions.personal_growth}` : '';

    const sections = [
      { title: 'Numerology Profile', content: numerologyPdfHeader },
      { title: 'Chinese Zodiac Compatibility', content: chineseCompatPdf },
      { title: 'Western Zodiac Compatibility', content: westernCompatPdf },
      { title: 'Complete Compatibility Analysis', content: reading.numerology_compatibility },
      { title: 'Feng Shui Directions', content: fengShuiPdfContent },
      { title: 'Wealth Hack', content: reading.wealth_hack },
      { title: 'Wealth Blueprint', content: reading.wealth_blueprint },
      { title: '2026 Prediction', content: reading.prediction_2026 },
      { title: '2026 Actions', content: reading.actions_2026 },
      { title: '2027 Prediction', content: reading.prediction_2027 },
      { title: '2027 Actions', content: reading.actions_2027 },
      { title: '2028 Prediction', content: reading.prediction_2028 },
      { title: '2028 Actions', content: reading.actions_2028 },
      { title: 'Life Purpose', content: reading.life_purpose },
      { title: 'Soul Contract', content: reading.soul_contract },
      { title: 'Challenges', content: reading.challenges },
      { title: 'Best Days', content: reading.best_days },
      { title: 'Personal Money Code', content: reading.money_code },
      { title: 'Career Choices', content: reading.career_choices },
      { title: 'Lucky Numbers', content: reading.lucky_numbers },
      { title: 'Love Language', content: reading.love_language },
      { title: 'Gifts', content: reading.gifts },
    ];

    sections.forEach((section) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(section.title, margin, yPosition);
      yPosition += 7;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const lines = doc.splitTextToSize(section.content || 'N/A', maxWidth);
      lines.forEach((line) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      yPosition += 5;
    });

    doc.save(`numerology-reading-${reading.name.replace(/\s+/g, '-')}.pdf`);
    toast.success('Reading downloaded successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void text-text-primary flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold animate-spin" />
      </div>
    );
  }

  if (!reading) {
    return null;
  }

  const numerologyHeader = `
🔢 Life Path Number: ${reading.life_path_number || 'N/A'}
🐉 Chinese Zodiac: ${reading.chinese_zodiac || 'N/A'}
♈ Western Zodiac: ${reading.western_zodiac || 'N/A'}
🧭 Feng Shui Qua Number: ${reading.qua_number} (${reading.feng_shui_element} Element)
  `;

  const chineseCompatInfo = reading.chinese_compatibility ? `
🐉 Chinese Zodiac Compatibility for ${reading.chinese_zodiac}:

✅ Best Matches: ${reading.chinese_compatibility.best.join(', ')}
⚠️ Challenging Matches: ${reading.chinese_compatibility.challenging.join(', ')}

Chinese astrology suggests these animals complement your energy patterns and life approach.
  ` : '';

  const westernCompatInfo = reading.western_compatibility ? `
♈ Western Zodiac Compatibility for ${reading.western_zodiac}:

✅ Best Matches: ${reading.western_compatibility.best.join(', ')}
⚠️ Challenging Matches: ${reading.western_compatibility.challenging.join(', ')}

These signs naturally harmonize with your astrological energy and communication style.
  ` : '';

  const fengShuiInfo = reading.feng_shui_directions ? `
Based on Marie Diamond's method, your auspicious directions are:
• Success & Wealth: ${reading.feng_shui_directions.success}
• Health & Well-being: ${reading.feng_shui_directions.health}
• Love & Romance: ${reading.feng_shui_directions.romance}
• Personal Growth: ${reading.feng_shui_directions.personal_growth}

Align your desk, bed, and important spaces to face these directions for maximum benefit.
  ` : '';

  const sections = [
    { title: 'Your Numerology Profile', content: numerologyHeader, icon: Sparkles },
    { title: '🐉 Chinese Zodiac Compatibility', content: chineseCompatInfo, icon: Sparkles },
    { title: '♈ Western Zodiac Compatibility', content: westernCompatInfo, icon: Sparkles },
    { title: '💕 Complete Numerology Compatibility', content: reading.numerology_compatibility, icon: Sparkles },
    { title: 'Feng Shui Energy Map', content: fengShuiInfo, icon: Sparkles },
    { title: '💰 Wealth Hack (Life Path Strategy)', content: reading.wealth_hack, icon: Sparkles },
    { title: '💎 Complete Wealth Blueprint', content: reading.wealth_blueprint, icon: Sparkles },
    { title: '🔮 2026 Prediction', content: reading.prediction_2026, icon: Sparkles },
    { title: '✨ Actions for 2026', content: reading.actions_2026, icon: Sparkles },
    { title: '🔮 2027 Prediction', content: reading.prediction_2027, icon: Sparkles },
    { title: '✨ Actions for 2027', content: reading.actions_2027, icon: Sparkles },
    { title: '🔮 2028 Prediction', content: reading.prediction_2028, icon: Sparkles },
    { title: '✨ Actions for 2028', content: reading.actions_2028, icon: Sparkles },
    { title: 'Life Purpose', content: reading.life_purpose, icon: Sparkles },
    { title: 'Soul Contract', content: reading.soul_contract, icon: Sparkles },
    { title: 'Challenges', content: reading.challenges, icon: Sparkles },
    { title: 'Best Days', content: reading.best_days, icon: Sparkles },
    { title: 'Personal Money Code', content: reading.money_code, icon: Sparkles },
    { title: 'Career Choices', content: reading.career_choices, icon: Sparkles },
    { title: 'Lucky Numbers', content: reading.lucky_numbers, icon: Sparkles },
    { title: 'Love Language', content: reading.love_language, icon: Sparkles },
    { title: 'Gifts', content: reading.gifts, icon: Sparkles },
    { title: '🗓️ Timing Guide: Specific Days to Implement and Manifest', content: reading.bonus_timing_guide, icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-void text-text-primary">
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="px-6 md:px-12 lg:px-24 py-6 flex justify-between items-center">
          <div>
            <h1 className="font-playfair text-xl md:text-2xl font-bold">{reading.name}</h1>
            <p className="text-text-secondary text-sm">{reading.birthday}</p>
          </div>
          <Button
            onClick={downloadPDF}
            className="bg-transparent border border-gold text-gold hover:bg-gold/10 rounded-full px-6 py-3"
            data-testid="download-pdf-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Reading Content */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          {sections.map((section, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="tracing-beam relative bg-void-paper border border-white/5 p-8 hover:border-gold/30 transition-colors"
              data-testid={`reading-section-${i}`}
            >
              <div className="flex items-start gap-4">
                <section.icon className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="font-playfair text-2xl font-bold mb-4 text-gold">{section.title}</h2>
                  <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                    {section.content || 'No content available'}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Share CTA */}
      <div className="px-6 md:px-12 lg:px-24 py-24 bg-void-paper">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center"
        >
          <Sparkles className="w-12 h-12 text-gold mx-auto mb-6" />
          <h2 className="font-playfair text-3xl font-bold mb-4">
            Discover Your Cosmic Path
          </h2>
          <p className="text-text-secondary mb-8">
            Your personalized numerology reading reveals the unique blueprint of your soul's journey.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-gold text-void hover:bg-gold-hover rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
            data-testid="scroll-top-button"
          >
            Back to Top
          </Button>
        </motion.div>
      </div>

      {/* Footer Disclaimer */}
      <footer className="bg-void border-t border-white/5 px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center text-text-secondary text-sm space-y-3">
            <p className="font-medium text-gold">
              Important Disclaimer
            </p>
            <p className="leading-relaxed">
              <strong className="text-text-primary">Personal Responsibility:</strong> You perform your own actions and are solely responsible for all outcomes based on your decisions. 
              This reading is provided for educational and entertainment purposes only.
            </p>
            <p className="text-xs text-text-muted pt-4 border-t border-white/5">
              The information provided should not be considered professional advice. Use this tool as a guide for self-reflection and personal growth.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ReadingPage;
